import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./SignupLogin.css";
import Footer from "./footer";
import { useAuth } from "../context/AuthContext";
import { getGuestCart, clearGuestCart } from "./CartService";


const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

async function syncGuestCart(token) {
  if (!token) {
    console.debug("syncGuestCart: no token");
    return false;
  }

  const guestCart = getGuestCart() || [];
  if (!Array.isArray(guestCart) || guestCart.length === 0) {
    console.debug("syncGuestCart: guest cart empty");
    return false;
  }

  console.debug("syncGuestCart: syncing", guestCart);

  let anySuccess = false;

  for (const gItem of guestCart) {
    const pid = gItem.product?.id ?? gItem.id;
    const qty = Number(gItem.quantity ?? 1);
    if (!pid) {
      console.warn("syncGuestCart: skipping item with no product id", gItem);
      continue;
    }

    try {
      // Use add endpoint for each guest item. Server should merge if product exists.
      const res = await fetch(`${API_BASE}/api/cart/add`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ product_id: pid, quantity: qty }),
      });

      const text = await res.text();
      console.debug("syncGuestCart: add response for pid", pid, res.status, text);

      if (res.ok) {
        anySuccess = true;
      } else {
        // non-OK: log it, but continue syncing other items
        console.warn("syncGuestCart: add failed", pid, res.status, text);
      }
    } catch (err) {
      console.error("syncGuestCart: network error for pid", pid, err);
    }
  }

  if (anySuccess) {
    try {
      clearGuestCart();
      console.debug("syncGuestCart: cleared guest cart after sync");
    } catch (e) {
      console.warn("syncGuestCart: failed to clear guest cart", e);
    }
    try { window.dispatchEvent(new Event("cart:updated")); } catch (e) {}
  } else {
    console.debug("syncGuestCart: no successful requests, leaving guest cart intact");
  }

  return anySuccess;
}


export default function SignupLogin({ onAuthSuccess }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [signupMethod, setSignupMethod] = useState("email");
  const [loginMethod, setLoginMethod] = useState("email");

  const navigate = useNavigate();
  const auth = useAuth(); // use context

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    // clear top-level error when user edits
    if (error) setError("");
  };

  // derived validation flags
  const passwordLength = formData.password.length;
  const passwordValid = passwordLength >= 8;
  const passwordsMatch = formData.password === formData.password_confirmation;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isLogin) {
        // build credentials
        const credentials =
          loginMethod === "email"
            ? { email: formData.email, password: formData.password }
            : { phone: formData.phone, password: formData.password };

        // call context login (this will store token + set user or fetch user)
        const res = await auth.login(credentials); // login throws on error

        const user = auth.user;
        await auth.login(credentials); // ensure login finished and token persisted

        // read token directly from localStorage (guaranteed persisted by auth context)
        const token = localStorage.getItem("token");
        if (token) {
          try {
            // await merging so the user sees merged cart immediately after navigation
            await syncGuestCart(token);
          } catch (err) {
            console.warn("syncGuestCart failed after login:", err);
          }
        }

        if (user?.is_admin) navigate("/cms");
        else navigate("/");

      } else {
        if (!passwordValid) {
          setError("رمز عبور باید حداقل ۸ کاراکتر باشد.");
          setLoading(false);
          return;
        }
        if (!passwordsMatch) {
          setError("رمز عبور و تکرار آن مطابقت ندارند.");
          setLoading(false);
          return;
        }

        // register payload
        const payload = {
          name: formData.name,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
        };
        if (signupMethod === "email") payload.email = formData.email;
        else payload.phone = formData.phone;

        // call context register
        const res = await auth.register(payload); // register throws on error
        if (auth.token) {
          await syncGuestCart(auth.token);
        }
        // navigate after register success
        const user = auth.user;
        if (user?.is_admin) navigate("/cms");
        else navigate("/");
      }

      // optional parent callback
      if (onAuthSuccess) onAuthSuccess();
    } catch (err) {
      // err is an Error thrown by auth.login/register
      setError(err?.message || "خطا در ارسال درخواست");
    } finally {
      setLoading(false);
    }
  };

  const formVariants = {
    initial: { opacity: 0, x: isLogin ? 50 : -50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: isLogin ? -50 : 50 },
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <motion.div
          className="auth-card"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <h2>{isLogin ? "ورود" : "ثبت‌نام"}</h2>

          {error && <p className="error">{error}</p>}

          {/* Method toggle */}
          {isLogin ? (
            <div className="signup-method-toggle" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className={`method-btn ${loginMethod === "email" ? "active" : ""}`}
                onClick={() => setLoginMethod("email")}
              >
                ورود با ایمیل
              </button>
              <button
                type="button"
                className={`method-btn ${loginMethod === "phone" ? "active" : ""}`}
                onClick={() => setLoginMethod("phone")}
              >
                ورود با شماره تلفن
              </button>
            </div>
          ) : (
            <div className="signup-method-toggle" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className={`method-btn ${signupMethod === "email" ? "active" : ""}`}
                onClick={() => setSignupMethod("email")}
              >
                ثبت‌نام با ایمیل
              </button>
              <button
                type="button"
                className={`method-btn ${signupMethod === "phone" ? "active" : ""}`}
                onClick={() => setSignupMethod("phone")}
              >
                ثبت‌نام با شماره تلفن
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.form
              key={isLogin ? `login-${loginMethod}` : `signup-${signupMethod}`}
              onSubmit={handleSubmit}
              variants={formVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.7 }}
            >
              {!isLogin && (
                <input
                  type="text"
                  name="name"
                  placeholder="نام و نام خانوادگی"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              )}

              {/* Email or phone for login */}
              {isLogin && loginMethod === "email" && (
                <input
                  type="email"
                  name="email"
                  placeholder="ایمیل"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              )}
              {isLogin && loginMethod === "phone" && (
                <input
                  type="text"
                  name="phone"
                  placeholder="شماره تلفن (مثال: 09121234567)"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              )}

              {/* Email for signup */}
              {!isLogin && signupMethod === "email" && (
                <input
                  type="email"
                  name="email"
                  placeholder="ایمیل"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              )}

              {/* Phone for signup */}
              {!isLogin && signupMethod === "phone" && (
                <input
                  type="text"
                  name="phone"
                  placeholder="شماره تلفن (مثال: 09121234567)"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              )}

              <input
                type="password"
                name="password"
                placeholder="رمز عبور"
                value={formData.password}
                onChange={handleChange}
                required
              />

              <div className="password-hint-row">
                {  /*
                <div className={`hint ${passwordValid ? "ok" : ""}`}>
                  رمز عبور باید حداقل ۸ کاراکتر باشد.
                </div>
                */}
                {/* show live error when user typed but too short */}
                {passwordLength > 0 && !passwordValid && !isLogin && (
                  <div className="field-error">رمز عبور باید حداقل شامل ۸ کاراکتر باشد!</div>
                )}
              </div>

              {!isLogin && (
                <input
                  type="password"
                  name="password_confirmation"
                  placeholder="تکرار رمز عبور"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                />
              )}

              <button
                type="submit"
                disabled={
                  loading ||
                  (!isLogin && (!passwordValid || !passwordsMatch))
                }
              >
                {loading ? "در حال پردازش..." : isLogin ? "ورود" : "ثبت‌نام"}
              </button>
            </motion.form>
          </AnimatePresence>

          <p className="toggle-text">
            {isLogin ? "حساب کاربری ندارید؟" : "قبلاً ثبت‌نام کرده‌اید؟"}{" "}
            <span
              onClick={() => {
                setIsLogin(!isLogin);
                setSignupMethod("email");
                setLoginMethod("email");
                setError("");
              }}
              style={{ cursor: "pointer", fontWeight: 700 }}
            >
              {isLogin ? "ثبت‌نام" : "ورود"}
            </span>
          </p>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
